import {
  Box,
  Button,
  Divider,
  FormControlLabel,
  Grid,
  InputAdornment,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
  CircularProgress,
  Tabs,
  Tab,
  Chip,
  Avatar,
} from "@mui/material";
import { Search as SearchIcon, User, Building } from "lucide-react";
import React, { useEffect, useMemo, useState } from "react";
import GenericModal from "src/components/generic-modal";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import MapComponent from "./Maps";
import CreateDriver from "./CreateDriver";
import {
  Drivers,
  assignDriverToManifest,
  assignCarrierToManifest,
  unassignDriverFromManifest,
  getCarriers,
  createWithDriver,
} from "src/queries/query";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check } from "@mui/icons-material";
import CommonAlert from "src/components/alert/CommonAlert";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import ManifestStartTimePicker from "src/components/apps/ManifestsComponents/ManifestStartTimePicker";

export default function AssignDriverCarrier({
  open,
  handleClose,
  leg,
  manifestData,
  preSelectedCarrier = null,
  isOSND = false,
  isLineHaul = false,
  refetchOrders,
}) {
  const { id } = useParams();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState(0);
  const [stop, setStop] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [newDriver, setNewDriver] = useState(false);
  const [selectedDrivers, setSelectedDrivers] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");

  // Determine if we're in leg context or manifest context
  const isLegContext = !manifestData;
  const isManifestContext = !!manifestData;

  // Carrier assignment state
  const [selectedCarrier, setSelectedCarrier] = useState(null);
  const [carrierSearchTerm, setCarrierSearchTerm] = useState("");
  const [startTime, setStartTime] = useState(null);

  const [alert, setAlert] = useState({
    open: false,
    message: "",
    severity: "error",
  });
  // Keep modal in loading state while manifest/order caches refetch after assign
  const [isRefreshing, setIsRefreshing] = useState(false);

  const getManifestUuidFromResponse = (response) =>
    response?.data?.data?.data?.uuid ||
    response?.data?.data?.uuid ||
    response?.data?.uuid ||
    response?.uuid ||
    null;

  const getManifestUuid = () =>
    id || manifestData?.uuid || manifestData?.id || null;

  const refreshAfterAssign = async (manifestUuid) => {
    const uuid = manifestUuid || getManifestUuid();
    const tasks = [
      queryClient.invalidateQueries({ queryKey: ["orders"] }),
      queryClient.invalidateQueries({ queryKey: ["manifests"] }),
    ];

    if (uuid) {
      tasks.push(
        queryClient.invalidateQueries({ queryKey: ["manifest", uuid] }),
      );
    }

    if (id) {
      tasks.push(
        queryClient.invalidateQueries({ queryKey: ["order", id] }),
        queryClient.invalidateQueries({ queryKey: ["order-id", id] }),
      );
    }

    if (typeof refetchOrders === "function") {
      tasks.push(Promise.resolve(refetchOrders()));
    }

    await Promise.allSettled(tasks);
  };

  const handleAlertClose = () => {
    setAlert((prev) => ({
      ...prev,
      open: false,
    }));
  };

  // React Query: Fetch carriers
  const {
    data: carriersData,
    isLoading: isLoadingCarriers,
    error: carriersError,
  } = useQuery({
    queryKey: ["carriers"],
    queryFn: getCarriers,
    enabled: Boolean(open && activeTab === 0),
    select: (data) => data?.data?.data?.data || [],
  });

  // React Query: Fetch drivers
  const { data: driversData, isLoading: isLoadingDrivers } = useQuery({
    queryKey: ["drivers"],
    queryFn: Drivers,
    enabled: Boolean(open && activeTab === 1),
    select: (data) => data?.data?.data?.data || [],
  });

  const createWithDriverMutation = useMutation({
    mutationFn: ({
      driverUuid,
      carrierUuid,
      legUuid,
      isOSND,
      isLineHaul,
      startTime,
    }) =>
      createWithDriver({
        driverUuid,
        carrierUuid,
        legUuid,
        isOSND,
        isLineHaul,
        startTime,
      }),
    onSuccess: async (response) => {
      toast.success("Manifest created successfully!");
      try {
        const manifestUuid = getManifestUuidFromResponse(response);
        await refreshAfterAssign(manifestUuid);
        handleClose();
      } finally {
        setIsRefreshing(false);
      }
    },
    onError: (error) => {
      console.error("Error creating manifest:", error);
      setIsRefreshing(false);
      setAlert({
        open: true,
        message: error.message || "Failed to create manifest.",
        severity: "error",
      });
    },
  });

  // Mutation: Assign driver to existing manifest (for manifest context)
  const assignDriverMutation = useMutation({
    mutationFn: ({ manifestUuid, driverUuid }) =>
      assignDriverToManifest(manifestUuid, driverUuid),
    onSuccess: async (_data, variables) => {
      toast.success("Driver assigned successfully!");
      try {
        await refreshAfterAssign(variables?.manifestUuid);
        handleClose();
      } finally {
        setIsRefreshing(false);
      }
    },
    onError: (error) => {
      console.error("Error assigning driver:", error);
      setIsRefreshing(false);
      setAlert({
        open: true,
        message: error.message || "Failed to assign driver.",
        severity: "error",
      });
    },
  });

  // Mutation: Assign carrier to existing manifest (for manifest context)
  const assignCarrierMutation = useMutation({
    mutationFn: ({ manifestUuid, carrierUuid }) =>
      assignCarrierToManifest(manifestUuid, carrierUuid),
    onSuccess: async (_data, variables) => {
      toast.success("Carrier assigned successfully!");
      try {
        await refreshAfterAssign(variables?.manifestUuid);
        handleClose();
      } finally {
        setIsRefreshing(false);
      }
    },
    onError: (error) => {
      console.error("Error assigning carrier:", error);
      setIsRefreshing(false);
      setAlert({
        open: true,
        message: error.message || "Failed to assign carrier.",
        severity: "error",
      });
    },
  });

  // Mutation: Unassign driver from manifest
  const unassignDriverMutation = useMutation({
    mutationFn: (manifestUuid) => unassignDriverFromManifest(manifestUuid),
    onSuccess: async (_data, manifestUuid) => {
      toast.success("Driver removed successfully!");
      try {
        await refreshAfterAssign(manifestUuid);
        // Don't close modal - user might want to assign another driver
      } finally {
        setIsRefreshing(false);
      }
    },
    onError: (error) => {
      console.error("Error removing driver:", error);
      setIsRefreshing(false);
      setAlert({
        open: true,
        message: error.message || "Failed to remove driver.",
        severity: "error",
      });
    },
  });

  // Filter carriers based on search
  const filteredCarriers = useMemo(() => {
    if (!carriersData) return [];
    const lowerSearch = (carrierSearchTerm || "").toLowerCase().trim();
    if (!lowerSearch) return carriersData;

    return carriersData.filter((carrier) => {
      const name =
        typeof carrier?.name === "string"
          ? carrier.name.toLowerCase()
          : typeof carrier?.carrierName === "string"
            ? carrier.carrierName.toLowerCase()
            : "";
      const dotId = carrier?.dotId ? String(carrier.dotId).toLowerCase() : "";
      const mcId = carrier?.mcId ? String(carrier.mcId).toLowerCase() : "";
      const city =
        carrier?.addresses?.[0]?.city &&
        typeof carrier.addresses[0].city === "string"
          ? carrier.addresses[0].city.toLowerCase()
          : "";
      const services =
        Array.isArray(carrier?.services) && carrier.services.length > 0
          ? carrier.services.map((s) => (s?.name || "").toLowerCase()).join(" ")
          : "";

      return (
        name.includes(lowerSearch) ||
        dotId.includes(lowerSearch) ||
        mcId.includes(lowerSearch) ||
        city.includes(lowerSearch) ||
        services.includes(lowerSearch)
      );
    });
  }, [carriersData, carrierSearchTerm]);

  // Filter drivers based on search
  const filteredDrivers = useMemo(() => {
    if (!driversData) return [];
    const query = searchQuery.toLowerCase();
    return driversData.filter(
      (driver) =>
        driver.firstName?.toLowerCase().includes(query) ||
        driver.lastName?.toLowerCase().includes(query),
    );
  }, [driversData, searchQuery]);

  // Reset state when modal opens/closes and set default driver if available
  useEffect(() => {
    if (open) {
      setStartTime(null);
      // Normalize incoming preSelectedCarrier to the shape this modal expects
      if (preSelectedCarrier) {
        const normalizedCarrier = {
          ...preSelectedCarrier,
          name:
            preSelectedCarrier.name ||
            preSelectedCarrier.carrierName ||
            preSelectedCarrier.carrier?.name ||
            "",
          uuid:
            preSelectedCarrier.uuid ||
            preSelectedCarrier.carrierUuid ||
            preSelectedCarrier.id ||
            preSelectedCarrier.carrierId ||
            null,
          id:
            preSelectedCarrier.id ||
            preSelectedCarrier.carrierId ||
            preSelectedCarrier.uuid ||
            null,
          tariffCost:
            preSelectedCarrier.tariffCost ||
            preSelectedCarrier.carrierTariffCost ||
            preSelectedCarrier.carrierCost ||
            0,
        };

        setSelectedCarrier(normalizedCarrier);
      } else {
        setSelectedCarrier(null);
      }

      setSelectedDrivers(null);
      setCarrierSearchTerm("");
      setSearchQuery("");
      setStop(false);
      setSelectedLocation(null);

      // Set default driver if manifestData contains a driver and we're on the driver tab
      if (manifestData?.driver && activeTab === 1) {
        setSelectedDrivers(manifestData.driver);
      }

      // If we're opening the modal and there's already a driver, switch to driver tab
      if (manifestData?.driver) {
        setActiveTab(1);
        setSelectedDrivers(manifestData.driver);
      } else {
        // Default to carrier tab only if no driver is present
        setActiveTab(0);
      }
    }
  }, [open, manifestData, preSelectedCarrier]);

  // Handler functions
  const handleOpenDriver = () => {
    setNewDriver(true);
  };

  const handleCloseDriver = () => {
    setNewDriver(false);
  };

  const handleDriverCreated = () => {
    queryClient.invalidateQueries(["drivers"]);
  };

  const handleChange = (event) => {
    const locationId = event.target.value;
    const locationObj = locations.find((loc) => loc.id === locationId);
    setSelectedLocation(locationObj || null);
  };

  const locations = [
    {
      id: "county-transport",
      name: "County Transport",
      address: "250 Sidney Street",
      city: "Belleville, ON K8P 5L6 Canada",
      coordinates: "44.162599, -77.401419",
      longitude: -77.401419,
      latitude: 44.162599,
    },
    {
      id: "weatherhaven",
      name: "Weatherhaven",
      address: "100 Strowger Boulevard",
      city: "Brockville, ON K6V 5X1 Canada",
      coordinates: "44.591839, -75.703163",
      longitude: -75.703163,
      latitude: 44.591839,
    },
  ];

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    // Set default driver when switching to driver tab if manifestData contains a driver
    if (newValue === 1 && manifestData?.driver && !selectedDrivers) {
      setSelectedDrivers(manifestData.driver);
    }
  };

  const handleOpenStop = () => {
    setStop(true);
  };

  const handleCloseStop = () => {
    setStop(false);
  };

  const handleSelectDriver = (driver) => {
    setSelectedDrivers(driver);
  };

  const handleRemoveDriver = () => {
    if (isManifestContext && id) {
      setIsRefreshing(true);
      // Call API to unassign driver from manifest
      unassignDriverMutation.mutate(id);
    } else {
      setSelectedDrivers(null);
    }
  };

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };

  const handleAssignDriver = () => {
    if (!selectedDrivers) {
      toast.error("Please select a driver");
      return;
    }

    setIsRefreshing(true);
    if (isLegContext) {
      createWithDriverMutation.mutate({
        driverUuid: selectedDrivers.uuid,
        legUuid: leg?.uuid,
        isOSND,
        isLineHaul,
        startTime: startTime || undefined,
      });
    } else {
      // Assign driver to existing manifest
      const manifestUuid = getManifestUuid();
      if (!manifestUuid) {
        setIsRefreshing(false);
        toast.error("No manifest selected");
        return;
      }
      assignDriverMutation.mutate({
        manifestUuid,
        driverUuid: selectedDrivers.uuid,
      });
    }
  };

  const handleAssignCarrier = () => {
    if (!selectedCarrier) {
      toast.error("Please select a carrier");
      return;
    }

    setIsRefreshing(true);
    if (isLegContext) {
      createWithDriverMutation.mutate({
        carrierUuid: selectedCarrier.uuid || selectedCarrier.id,
        legUuid: leg?.uuid,
        isOSND,
        isLineHaul,
        startTime: startTime || undefined,
      });
    } else {
      const manifestUuid = getManifestUuid();
      if (!manifestUuid) {
        setIsRefreshing(false);
        toast.error("No manifest selected");
        return;
      }
      assignCarrierMutation.mutate({
        manifestUuid,
        carrierUuid: selectedCarrier.uuid || selectedCarrier.id,
      });
    }
  };
  const canAssignCarrier =
    activeTab === 0 && selectedCarrier && (isLegContext ? true : Boolean(id));
  const canAssignDriver =
    activeTab === 1 && selectedDrivers && (isLegContext ? true : Boolean(id));
  const isAssigning =
    createWithDriverMutation.isPending ||
    assignDriverMutation.isPending ||
    assignCarrierMutation.isPending ||
    unassignDriverMutation.isPending ||
    isRefreshing;

  return (
    <GenericModal
      open={open}
      onClose={isAssigning ? () => {} : handleClose}
      title={`${isLegContext ? "Create" : "Assign"}${isLineHaul ? " Line Haul" : ""} Manifest${leg?.legNumber ? ` to Leg ${leg.legNumber}` : ""}`}
      secondaryButtonText="Cancel"
      primaryButtonText={
        isAssigning ? (
          <CircularProgress size={24} color="inherit" />
        ) : isLegContext ? (
          `Create${isLineHaul ? " Line Haul" : ""} Manifest`
        ) : (
          "Assign"
        )
      }
      maxWidth="md"
      fullWidth={true}
      desktopWidth="900px"
      primaryButtonProps={{
        disabled: isAssigning || (!canAssignCarrier && !canAssignDriver),
      }}
      secondaryButtonProps={{
        disabled: isAssigning,
      }}
      modalIcon={isLegContext ? <Building size={18} /> : <User size={18} />}
      onPrimaryAction={() => {
        if (isAssigning) return;
        if (activeTab === 0) {
          handleAssignCarrier();
        } else if (activeTab === 1) {
          handleAssignDriver();
        }
      }}
    >
      <Box sx={{ minHeight: "500px", maxHeight: "700px", py: 0 }}>
        {isLegContext && (
          <Box sx={{ mb: 2, px: 0.5 }}>
            <ManifestStartTimePicker
              label="Manifest Start Date & Time"
              value={startTime}
              onChange={setStartTime}
              showClear
            />
          </Box>
        )}
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          sx={{
            pt: 0,
            borderBottom: 1,
            borderColor: "divider",
            mb: 1,
            "& .MuiTab-root": {
              textTransform: "none",
              fontWeight: 600,
              fontSize: "0.95rem",
            },
          }}
        >
          <Tab
            label={`Assign${isLineHaul ? " Line Haul" : ""} Carrier`}
            sx={{ p: "0 10px" }}
          />
          <Tab
            label={`Assign${isLineHaul ? " Line Haul" : ""} Driver`}
            sx={{ p: "0 10px" }}
          />
        </Tabs>

        {/* Assign Carrier Tab */}
        {activeTab === 0 && (
          <Box>
            {/* Carrier Selection */}
            {!selectedCarrier && (
              <Box>
                <TextField
                  fullWidth
                  variant="outlined"
                  placeholder="Search carriers by name..."
                  size="small"
                  value={carrierSearchTerm}
                  onChange={(e) => setCarrierSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon size={20} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ mb: 2 }}
                />

                {isLoadingCarriers ? (
                  <Box
                    sx={{ display: "flex", justifyContent: "center", py: 4 }}
                  >
                    <CircularProgress />
                  </Box>
                ) : carriersError ? (
                  <Box sx={{ textAlign: "center", py: 4 }}>
                    <Typography color="error">
                      Error loading carriers. Please try again.
                    </Typography>
                  </Box>
                ) : filteredCarriers.length > 0 ? (
                  <Box
                    sx={{
                      maxHeight: "400px",
                      overflowY: "auto",
                      display: "flex",
                      flexDirection: "column",
                      gap: 1.5,
                    }}
                  >
                    {filteredCarriers.map((carrier) => (
                      <Paper
                        key={carrier.uuid || carrier.id}
                        onClick={() => setSelectedCarrier(carrier)}
                        sx={{
                          p: 1,

                          border: "1px solid",
                          borderColor: "divider",
                          borderRadius: 1,
                          cursor: "pointer",
                          transition: "all 0.2s",
                          "&:hover": {
                            borderColor: "primary.main",
                            boxShadow: 1,
                            // transform: 'translateY(-2px)',
                          },
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 2,
                            flexDirection: "row",
                          }}
                        >
                          <Avatar
                            sx={{
                              bgcolor: "primary.main",
                              width: 38,
                              height: 38,
                              fontWeight: "bold",
                            }}
                          >
                            {carrier?.name?.charAt(0)?.toUpperCase() || "C"}
                          </Avatar>
                          <Box
                            sx={{
                              flex: 1,
                              display: "flex",
                              flexDirection: "row",
                              alignItems: "center",
                              justifyContent: "space-between",
                            }}
                          >
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                              {carrier?.name || "N/A"}
                            </Typography>
                            <Box
                              sx={{
                                display: "flex",
                                gap: 2,
                                flexDirection: "row",
                              }}
                            >
                              {carrier?.addresses?.[0]?.city && (
                                <Chip
                                  label={`📍 ${carrier.addresses[0].city}`}
                                  size="small"
                                  variant="outlined"
                                />
                              )}
                              {carrier?.dotId && (
                                <Chip
                                  label={`DOT: ${carrier.dotId}`}
                                  size="small"
                                  variant="outlined"
                                />
                              )}
                              {carrier?.mcId && (
                                <Chip
                                  label={`MC: ${carrier.mcId}`}
                                  size="small"
                                  variant="outlined"
                                />
                              )}
                            </Box>
                            {carrier?.services &&
                              carrier.services.length > 0 && (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{ mt: 0.5, display: "block" }}
                                >
                                  Services:{" "}
                                  {carrier.services
                                    .map((s) => s?.name)
                                    .filter(Boolean)
                                    .join(", ")}
                                </Typography>
                              )}
                          </Box>
                        </Box>
                      </Paper>
                    ))}
                  </Box>
                ) : (
                  <Box sx={{ textAlign: "center", py: 4 }}>
                    <Typography color="text.secondary">
                      {carrierSearchTerm
                        ? "No carriers found matching your search."
                        : "No carriers available."}
                    </Typography>
                  </Box>
                )}
              </Box>
            )}

            {selectedCarrier && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                  Selected Carrier
                </Typography>
                <Paper
                  sx={{
                    p: 1,
                    border: "2px solid",
                    borderColor: "primary.main",
                    borderRadius: 1,
                    // bgcolor: "action.selected",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Avatar
                      sx={{
                        bgcolor: "primary.main",
                        width: 40,
                        height: 40,
                        fontWeight: "bold",
                      }}
                    >
                      {selectedCarrier?.name?.charAt(0)?.toUpperCase() || "C"}
                    </Avatar>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {selectedCarrier?.name || "N/A"}
                    </Typography>
                  </Box>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => setSelectedCarrier(null)}
                  >
                    Change Carrier
                  </Button>
                </Paper>
              </Box>
            )}
          </Box>
        )}

        {/* Assign Driver Tab */}
        {activeTab === 1 && (
          <Box>
            {stop ? (
              <Grid container spacing={1}>
                <Grid sx={{ maxWidth: 500, mx: "auto", p: 2 }} item sm={7}>
                  <Button
                    variant="outlined"
                    startIcon={<ArrowBackIcon />}
                    sx={{ mb: 3, textTransform: "none", px: 2, py: 0.5 }}
                    onClick={handleCloseStop}
                  >
                    Back
                  </Button>

                  <Typography variant="h6" fontWeight="600" sx={{ mb: 1 }}>
                    Where does the manifest begin?
                  </Typography>

                  <Typography
                    variant="body1"
                    color="text.secondary"
                    sx={{ mb: 2 }}
                  >
                    To capture deadhead miles, add the location where the driver
                    will start driving from below:
                  </Typography>

                  <Paper
                    variant="outlined"
                    sx={{
                      maxHeight: 300,
                      overflow: "auto",
                      "&::-webkit-scrollbar": {
                        width: "8px",
                      },
                      "&::-webkit-scrollbar-thumb": {
                        backgroundColor: "#E0E0E0",
                        borderRadius: "4px",
                      },
                    }}
                  >
                    <RadioGroup
                      value={selectedLocation ? selectedLocation.id : ""}
                      onChange={handleChange}
                    >
                      {locations.map((location, index) => (
                        <React.Fragment key={location.id}>
                          <Box
                            sx={{
                              p: 2,
                              "&:hover": { bgcolor: "rgba(0, 0, 0, 0.04)" },
                            }}
                          >
                            <FormControlLabel
                              value={location.id}
                              control={<Radio />}
                              label={
                                <Stack spacing={0.5} sx={{ ml: 1 }}>
                                  <Typography variant="body1" fontWeight={500}>
                                    {location.name}
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                  >
                                    {location.address}
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                  >
                                    {location.city}
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    fontSize="0.875rem"
                                  >
                                    {location.coordinates}
                                  </Typography>
                                </Stack>
                              }
                              sx={{
                                alignItems: "flex-start",
                                margin: 0,
                                width: "100%",
                                ".MuiFormControlLabel-label": { width: "100%" },
                              }}
                            />
                          </Box>
                          {index < locations.length - 1 && <Divider />}
                        </React.Fragment>
                      ))}
                    </RadioGroup>
                  </Paper>
                </Grid>
                <Grid item sx={{ height: "full" }} sm={5}>
                  <MapComponent
                    latitude={selectedLocation?.latitude}
                    longitude={selectedLocation?.longitude}
                    height="100%"
                  />
                </Grid>
              </Grid>
            ) : (
              <Box>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 3,
                  }}
                >
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                      Select Driver
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Search and select a driver to{" "}
                      {isLegContext
                        ? "create a manifest with"
                        : "assign to this manifest"}
                      .
                    </Typography>
                  </Box>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleOpenDriver}
                    sx={{ textTransform: "none" }}
                  >
                    Create New Driver
                  </Button>
                </Box>

                <CreateDriver
                  open={newDriver}
                  handleClose={handleCloseDriver}
                  onSuccess={handleDriverCreated}
                />

                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 2,
                        height: "100%",
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 1,
                      }}
                    >
                      <Typography
                        variant="subtitle1"
                        sx={{ mb: 1, fontWeight: 600 }}
                      >
                        Available Drivers
                      </Typography>

                      <TextField
                        variant="outlined"
                        fullWidth
                        placeholder="Search drivers by name..."
                        size="small"
                        value={searchQuery}
                        onChange={handleSearchChange}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <SearchIcon size={20} />
                            </InputAdornment>
                          ),
                        }}
                        sx={{ mb: 2 }}
                      />

                      {isLoadingDrivers ? (
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "center",
                            py: 4,
                          }}
                        >
                          <CircularProgress />
                        </Box>
                      ) : filteredDrivers?.length > 0 ? (
                        <Box
                          sx={{
                            maxHeight: "350px",
                            overflowY: "auto",
                            display: "flex",
                            flexDirection: "column",
                            gap: 1,
                          }}
                        >
                          {filteredDrivers.map((driver) => (
                            <Paper
                              key={driver.uuid || driver.id}
                              onClick={() => handleSelectDriver(driver)}
                              sx={{
                                p: 1,
                                border: "1px solid",
                                borderColor:
                                  selectedDrivers?.uuid === driver.uuid
                                    ? "primary.main"
                                    : "divider",
                                borderRadius: 1,
                                cursor: "pointer",
                                // bgcolor:
                                //   selectedDrivers?.uuid === driver.uuid
                                //     ? 'action.selected'
                                //     : 'background.paper',
                                transition: "all 0.2s",
                                "&:hover": {
                                  borderColor: "primary.main",
                                  boxShadow: 1,
                                },
                              }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "space-between",
                                }}
                              >
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1.5,
                                  }}
                                >
                                  <Avatar
                                    sx={{
                                      bgcolor: "primary.main",
                                      width: 40,
                                      height: 40,
                                      fontWeight: "bold",
                                      fontSize: 14,
                                    }}
                                  >
                                    {driver?.firstName
                                      ?.charAt(0)
                                      ?.toUpperCase() || ""}
                                    {driver?.lastName
                                      ?.charAt(0)
                                      ?.toUpperCase() || ""}
                                  </Avatar>
                                  <Typography
                                    variant="body1"
                                    sx={{ fontWeight: 500 }}
                                  >
                                    {driver?.firstName} {driver?.lastName}
                                  </Typography>
                                </Box>
                                {selectedDrivers?.uuid === driver.uuid && (
                                  <Check color="primary" />
                                )}
                              </Box>
                            </Paper>
                          ))}
                        </Box>
                      ) : (
                        <Box sx={{ textAlign: "center", py: 4 }}>
                          <Typography color="text.secondary">
                            {searchQuery
                              ? "No drivers found matching your search."
                              : "No drivers available."}
                          </Typography>
                        </Box>
                      )}
                    </Paper>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 2,
                        height: "100%",
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 1,
                      }}
                    >
                      <Typography
                        variant="subtitle1"
                        sx={{ mb: 1, fontWeight: 600 }}
                      >
                        Selected Driver
                      </Typography>

                      {selectedDrivers ? (
                        <Paper
                          sx={{
                            p: 1,
                            border: "1px solid",
                            borderColor: "primary.main",
                            borderRadius: 1,
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1.5,
                              }}
                            >
                              <Avatar
                                sx={{
                                  bgcolor: "primary.main",
                                  width: 40,
                                  height: 40,
                                  fontWeight: "bold",
                                  fontSize: 14,
                                }}
                              >
                                {selectedDrivers?.firstName
                                  ?.charAt(0)
                                  ?.toUpperCase() || ""}
                                {selectedDrivers?.lastName
                                  ?.charAt(0)
                                  ?.toUpperCase() || ""}
                              </Avatar>
                              <Box>
                                <Typography
                                  variant="body1"
                                  sx={{ fontWeight: 600 }}
                                >
                                  {selectedDrivers?.firstName}{" "}
                                  {selectedDrivers?.lastName}
                                </Typography>
                              </Box>
                            </Box>
                            <Button
                              size="small"
                              variant="outlined"
                              color="error"
                              onClick={handleRemoveDriver}
                              disabled={unassignDriverMutation.isPending}
                              startIcon={
                                unassignDriverMutation.isPending ? (
                                  <CircularProgress size={16} color="inherit" />
                                ) : null
                              }
                              sx={{ minWidth: "auto", px: 1 }}
                            >
                              {unassignDriverMutation.isPending
                                ? "Removing..."
                                : "Remove"}
                            </Button>
                          </Box>
                        </Paper>
                      ) : (
                        <Box
                          sx={{
                            textAlign: "center",
                            py: 6,
                            border: "2px dashed",
                            borderColor: "divider",
                            borderRadius: 1,
                          }}
                        >
                          <Typography variant="body2" color="text.secondary">
                            No driver selected
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: "block", mt: 0.5 }}
                          >
                            Select a driver from the list to assign
                          </Typography>
                        </Box>
                      )}
                    </Paper>
                  </Grid>
                </Grid>

                {/* <Box
                  sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}
                >
                  <Button
                    variant="outlined"
                    startIcon={<ArrowBackIcon />}
                    onClick={handleOpenStop}
                    sx={{ textTransform: "none" }}
                  >
                    Add Starting Location
                  </Button>
                </Box> */}
              </Box>
            )}
          </Box>
        )}
      </Box>
      <CommonAlert
        open={alert.open}
        onClose={handleAlertClose}
        message={alert.message}
        severity={alert.severity}
      />
    </GenericModal>
  );
}
